import React, { useState, useRef, useEffect } from 'react';
import { DashboardFilters } from '../../types';
import { formatAccountName, etTodayParts, toYMD } from '../../utils/formatters';
import './Filters.css';

interface FiltersProps {
  filters: DashboardFilters;
  accounts: string[];
  onFilterChange: (filters: DashboardFilters) => void;
  onApply: () => void;
  onClear: () => void;
}

type DateRange = 'today' | 'yesterday' | 'week' | 'month' | 'quarter' | 'thisMonth' | 'lastMonth';

export const Filters: React.FC<FiltersProps> = ({
  filters,
  accounts,
  onFilterChange,
  onApply,
  onClear
}) => {
  const [accountSearch, setAccountSearch] = useState('');
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
        setAccountSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredAccounts = accounts.filter((a) =>
    formatAccountName(a).toLowerCase().includes(accountSearch.toLowerCase())
  );

  const setDateRange = (range: DateRange) => {
    const { year, month, day } = etTodayParts();
    const today = new Date(year, month - 1, day);
    const endDate = toYMD(today);
    let startDate = endDate;

    switch (range) {
      case 'today':
        break;
      case 'yesterday':
        const yesterday = new Date(year, month - 1, day - 1);
        startDate = toYMD(yesterday);
        onFilterChange({ ...filters, startDate, endDate: startDate });
        onApply();
        return;
      case 'week':
        startDate = toYMD(new Date(year, month - 1, day - 7));
        break;
      case 'month':
        startDate = toYMD(new Date(year, month - 1, day - 30));
        break;
      case 'quarter':
        startDate = toYMD(new Date(year, month - 1, day - 90));
        break;
      case 'thisMonth':
        startDate = toYMD(new Date(year, month - 1, 1));
        break;
      case 'lastMonth':
        const firstOfLastMonth = new Date(year, month - 2, 1);
        const lastOfLastMonth = new Date(year, month - 1, 0);
        onFilterChange({
          ...filters,
          startDate: toYMD(firstOfLastMonth),
          endDate: toYMD(lastOfLastMonth),
        });
        onApply();
        return;
    }

    onFilterChange({ ...filters, startDate, endDate });
    onApply();
  };

  return (
    <div className="filters">
      <h2 className="filters-title">Filter Data</h2>

      <div className="filter-row">
        <div className="filter-group" ref={accountRef}>
          <label>Account</label>
          <div className="searchable-select" onClick={() => setAccountOpen((o) => !o)}>
            <span className="searchable-select-value">
              {formatAccountName(filters.account)}
            </span>
            <span className="searchable-select-arrow">{accountOpen ? '▲' : '▼'}</span>
          </div>
          {accountOpen && (
            <div className="searchable-dropdown">
              <input
                className="searchable-dropdown-input"
                autoFocus
                placeholder="Search accounts..."
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <ul className="searchable-dropdown-list">
                {filteredAccounts.length === 0 ? (
                  <li className="searchable-dropdown-empty">No results</li>
                ) : (
                  filteredAccounts.map((account) => (
                    <li
                      key={account}
                      className={`searchable-dropdown-item${filters.account === account ? ' selected' : ''}`}
                      onClick={() => {
                        onFilterChange({ ...filters, account });
                        setAccountOpen(false);
                        setAccountSearch('');
                      }}
                    >
                      {formatAccountName(account)}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>

        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => onFilterChange({ ...filters, startDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => onFilterChange({ ...filters, endDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <button className="btn btn-primary" onClick={onApply}>
            Apply
          </button>
        </div>

        <div className="filter-group">
          <button className="btn btn-secondary" onClick={onClear}>
            Clear
          </button>
        </div>
      </div>

      <div className="quick-filters">
        <strong>Quick:</strong>
        <button className="quick-filter-btn" onClick={() => setDateRange('today')}>
          Today
        </button>
        <button className="quick-filter-btn" onClick={() => setDateRange('yesterday')}>
          Yesterday
        </button>
        <button className="quick-filter-btn" onClick={() => setDateRange('week')}>
          Last 7 Days
        </button>
        <button className="quick-filter-btn" onClick={() => setDateRange('month')}>
          Last 30 Days
        </button>
        <button className="quick-filter-btn" onClick={() => setDateRange('thisMonth')}>
          This Month
        </button>
        <button className="quick-filter-btn" onClick={() => setDateRange('lastMonth')}>
          Last Month
        </button>
        <button className="quick-filter-btn" onClick={() => setDateRange('quarter')}>
          Last 90 Days
        </button>
      </div>
    </div>
  );
};
